import React, { useMemo, useState, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Flex, Text, Spacer } from '@chakra-ui/react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

// Utility function to generate column definitions
const generateColumnDefs = (data) => {
  if (!data || data.length === 0) return [];

  const sampleRow = data[0];
  return Object.keys(sampleRow).map((key) => {
    const isNumber = typeof sampleRow[key] === 'number';

    return {
      headerName: key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim(),
      field: key,
      sortable: true,
      filter: isNumber ? 'agNumberColumnFilter' : true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
      resizable: true,
    };
  });
};

const DEFAULT_HEIGHT = '300px';

const RankingGrid = ({ height = DEFAULT_HEIGHT, gridData }) => {
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredRecords, setFilteredRecords] = useState(0);
  const [gridApi, setGridApi] = useState(null);

  // Memoize column definitions to optimize performance
  const columnDefs = useMemo(() => generateColumnDefs(gridData), [gridData]);

  // Handle grid ready event to store the grid API
  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  // Update the total and filtered records count when the data is rendered
  const onFirstDataRendered = useCallback((params) => {
    const api = params.api;
    setTotalRecords(api.getDisplayedRowCount()); // Set total records
    setFilteredRecords(api.getDisplayedRowCount()); // Initially, filtered records = total records
  }, []);

  // Update filtered record count when filters change
  const onFilterChanged = useCallback(() => {
    if (gridApi) {
      setFilteredRecords(gridApi.getDisplayedRowCount());
    }
  }, [gridApi]);

  // Download the grid data as a CSV file
  const onDownload = useCallback(() => {
    if (gridApi) {
      gridApi.exportDataAsCsv();
    }
  }, [gridApi]);

  return (
    <>
      {height !== DEFAULT_HEIGHT && (
        <Flex minWidth="max-content" alignItems="center" mb="1">
          <Text fontSize="sm">Total Records: {totalRecords},</Text>
          <Text ml="2" fontSize="sm">
            Filtered Records: {filteredRecords}
          </Text>
          <Spacer />
          <Button size="small" onClick={onDownload} icon={<DownloadOutlined />}>
            Download as csv
          </Button>
        </Flex>
      )}

      <div className="ag-theme-alpine" style={{ height, width: '100%' }}>
        <AgGridReact
          rowData={gridData}
          columnDefs={columnDefs}
          defaultColDef={{ sortable: true, filter: true }}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered} // Set total records after data is rendered
          onFilterChanged={onFilterChanged} // Update filtered records when filters change
          gridOptions={{
            enableCellTextSelection: true,
          }}
        />
      </div>
    </>
  );
};

export default RankingGrid;
